import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiPause, FiPlay } from "react-icons/fi";
import { productPlaceholder } from "../../data/products";
import ProductImage from "./ProductImage";

const AUTO_ROTATE_DELAY = 15000;
const IMAGE_TRANSITION_DURATION = 480;

export default function ProductGallery({ images = [], productName }) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [transition, setTransition] = useState(null);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [autoplayReset, setAutoplayReset] = useState(0);
  const touchStart = useRef(null);
  const viewportRef = useRef(null);
  const displayedImage = useRef(null);
  const selectionId = useRef(0);
  const galleryImages = images.length ? images : [productPlaceholder];
  const activeIndex = Math.min(selectedIndex, galleryImages.length - 1);
  const activeImage = galleryImages[activeIndex];
  const activeSelectionId = selectionId.current;
  const hasMultiple = galleryImages.length > 1;
  const imageCount = galleryImages.length;
  const nextImageSrc = galleryImages[(activeIndex + 1) % imageCount].src;

  useEffect(() => {
    if (!hasMultiple) return;
    const nextPhoto = new Image();
    nextPhoto.decoding = "async";
    nextPhoto.src = nextImageSrc;
  }, [hasMultiple, nextImageSrc]);

  useEffect(() => {
    if (!transition?.ready) return undefined;
    const id = transition.id;
    const timer = window.setTimeout(() => {
      setTransition((current) => current?.id === id ? null : current);
    }, IMAGE_TRANSITION_DURATION);
    return () => window.clearTimeout(timer);
  }, [transition?.id, transition?.ready]);

  const selectImage = useCallback((index, direction) => {
    // Even choosing the current thumbnail gives it a fresh viewing interval.
    setAutoplayReset((current) => current + 1);
    const nextIndex = Math.max(0, Math.min(index, imageCount - 1));
    if (nextIndex === activeIndex) return;

    // A prerendered photo may finish loading before React attaches onLoad.
    if (!displayedImage.current) {
      const photo = viewportRef.current?.querySelector(".product-gallery-current img");
      if (photo?.complete && photo.naturalWidth > 0) {
        displayedImage.current = { ...activeImage, src: photo.getAttribute("src"), alt: photo.alt };
      }
    }

    const id = ++selectionId.current;
    const animate = window.matchMedia("(prefers-reduced-motion: no-preference)").matches;
    setTransition(animate && displayedImage.current ? {
      id,
      from: displayedImage.current,
      direction: direction ?? (nextIndex > activeIndex ? 1 : -1),
      ready: false,
    } : null);
    setSelectedIndex(nextIndex);
  }, [activeImage, activeIndex, imageCount]);

  useEffect(() => {
    if (!hasMultiple || !isAutoPlaying) return undefined;
    let timer;
    const restartTimer = () => {
      window.clearTimeout(timer);
      if (document.visibilityState !== "hidden") {
        timer = window.setTimeout(() => {
          selectImage((activeIndex + 1) % imageCount, 1);
        }, AUTO_ROTATE_DELAY);
      }
    };

    restartTimer();
    document.addEventListener("visibilitychange", restartTimer);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", restartTimer);
    };
  }, [activeIndex, autoplayReset, hasMultiple, imageCount, isAutoPlaying, selectImage]);

  const handleImageLoad = (event) => {
    if (activeSelectionId !== selectionId.current) return;
    // Keep the last loaded photo (or its fallback) under a cold incoming image.
    displayedImage.current = {
      ...activeImage,
      src: event.currentTarget.getAttribute("src"),
      alt: event.currentTarget.alt,
    };
    setTransition((current) => current?.id === activeSelectionId
      ? { ...current, ready: true }
      : current);
  };

  const startSwipe = (event) => {
    touchStart.current = event.touches.length === 1
      ? { x: event.touches[0].clientX, y: event.touches[0].clientY }
      : null;
  };
  const finishSwipe = (event) => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start || event.changedTouches.length !== 1 || event.touches.length) return;
    const dx = event.changedTouches[0].clientX - start.x;
    const dy = event.changedTouches[0].clientY - start.y;
    if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy)) {
      selectImage(activeIndex + (dx < 0 ? 1 : -1));
    }
  };

  return (
    <div className={`product-gallery${hasMultiple ? " has-multiple" : ""}`} role="region"
      aria-roledescription="carousel" aria-label={`${productName} images`}>
      {hasMultiple && (
        <div className="product-gallery-thumbnail-rail">
          <div className="product-gallery-thumbnails" role="group" aria-label="Choose a product image">
            {galleryImages.map((image, index) => (
              <button key={`${image.src}-${index}`} type="button" className="product-gallery-thumbnail"
                aria-label={`Show image ${index + 1} of ${galleryImages.length}`} aria-pressed={activeIndex === index}
                onClick={() => selectImage(index)}>
                <ProductImage image={image} eager={index === 0} decorative />
              </button>
            ))}
          </div>
        </div>
      )}
      <div className={`product-gallery-viewport${transition ? " is-changing" : ""}${transition?.ready ? " is-ready" : ""}`}
        ref={viewportRef}
        style={{
          "--gallery-enter-offset": transition?.direction === -1 ? "-8px" : "8px",
          "--gallery-transition-duration": `${IMAGE_TRANSITION_DURATION}ms`,
        }}
        onTouchStart={startSwipe} onTouchEnd={finishSwipe}
        onTouchCancel={() => { touchStart.current = null; }}>
        <ProductImage key={`${activeSelectionId}-${activeImage.src}`} image={activeImage} eager decoding="sync"
          className="product-gallery-current" onLoad={handleImageLoad} />
        {transition && (
          <ProductImage image={transition.from} eager decorative decoding="sync" className="product-gallery-outgoing" />
        )}
        {hasMultiple && (
          <button type="button" className="product-gallery-autoplay"
            aria-label={isAutoPlaying ? "Pause automatic image rotation" : "Start automatic image rotation"}
            title={isAutoPlaying ? "Pause slideshow" : "Play slideshow"}
            onClick={() => setIsAutoPlaying((current) => !current)}>
            {isAutoPlaying ? <FiPause aria-hidden="true" /> : <FiPlay aria-hidden="true" />}
          </button>
        )}
        {hasMultiple && (
          <div className="product-gallery-arrows">
            <button type="button" className="product-gallery-previous" aria-label="Previous product image"
              disabled={activeIndex === 0} onClick={() => selectImage(activeIndex - 1)}>
              <FiChevronLeft aria-hidden="true" />
            </button>
            <button type="button" className="product-gallery-next" aria-label="Next product image"
              disabled={activeIndex === galleryImages.length - 1} onClick={() => selectImage(activeIndex + 1)}>
              <FiChevronRight aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
      {hasMultiple && (
        <>
          <div className="product-gallery-dots" role="group" aria-label="Choose a product image">
            {galleryImages.map((image, index) => (
              <button key={`${image.src}-${index}`} type="button" aria-label={`Show image ${index + 1} of ${galleryImages.length}`}
                aria-current={activeIndex === index ? "true" : undefined} onClick={() => selectImage(index)}>
                <span aria-hidden="true" />
              </button>
            ))}
          </div>
          <span className="product-gallery-status visually-hidden" aria-live={isAutoPlaying ? "off" : "polite"} aria-atomic="true">
            Image {activeIndex + 1} of {galleryImages.length}
          </span>
        </>
      )}
    </div>
  );
}
